import ToggleAskPromotion from "../ToggleAskPromotion";
import { Dispatch, SetStateAction } from "react";
import RightDetailBox from "../RightDetailBox/RightDetailBox";
import { SubmitHandler, useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ProductKey } from "src/config/config";
import { getProductFromSessionStorage } from "src/utils/storage/auth";
import styles from "./CheckPromotion.module.scss";
import FormInput from "components/Common/Form/Input/FormInput";
import { Spinner } from "react-bootstrap";
import { useCheckPromotion } from "src/hooks/api/auth/useCheckPromotion";
import {
  CheckPromotionFormData,
  checkPromotionSchema,
} from "./CheckPromotion.schema";
import { createServicePremiumBill } from "src/service/pay";
import { useRouter } from "src/hooks/useRouter";
import { notifyError, notifySuccess } from "components/Common/Toast";
import PromotionInfo from "./PromotionInfo";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

interface ICheckPromotionProps {
  toggleAskPromotionOn: boolean;
  setToggleAskPromotionOn: Dispatch<SetStateAction<boolean>>;
}

const CheckPromotion = ({
  toggleAskPromotionOn,
  setToggleAskPromotionOn,
}: ICheckPromotionProps) => {
  const route = useRouter();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // react-hook-form 설정
  const methods = useForm<CheckPromotionFormData>({
    resolver: yupResolver(checkPromotionSchema),
    defaultValues: {
      promotionCode: "",
      referralId: "",
      issueAppList: [],
      packageName: "",
      products: [],
    },
    mode: "onChange",
    criteriaMode: "all", // 모든 유효성 검사 규칙을 확인
  });

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    setError,
    control,
    clearErrors,
    formState: { errors, isValid },
  } = methods;

  // 커스텀 훅 사용
  const {
    isLoading,
    checkPromotionMutation,
    data: checkPromotionCodeV2Res,
    error: promotionError,
    isError,
    isSuccess,
  } = useCheckPromotion({
    onSuccess: (data) => {
      // 응답 데이터를 폼에 설정
      if (data.nxacAppList && data.nxacAppList.length > 0) {
        const issueAppList = data.nxacAppList.map((app: any) => app.id);
        setValue("issueAppList", issueAppList);
      }

      // DSF 구독 정보 세팅
      if (data.drmPackageInfo) {
        setValue("packageName", data.drmPackageInfo.packageName);
        setValue("products", data.drmPackageInfo.products);
      }

      // referralId가 있으면 설정
      if (data.referralId) {
        setValue("referralId", data.referralId);
      }

      // 성공 시 에러 제거
      clearErrors("promotionCode");
    },
    onError: (error: any) => {
      console.error("프로모션 코드 확인 에러:", error);

      // 실패 시 에러 설정
      setError("promotionCode", {
        type: "validation",
        message: t("siteType.premium.detail.plan.invalidPromotionCode"),
      });

      if (error?.errorCode === "OVER_VALIDITY_TIME") {
        setToggleAskPromotionOn((prev) => !prev);
      }
    },
  });

  const watchedPromotionCode = watch("promotionCode");

  // react-hook-form의 에러 상태를 기반으로 한 성공/실패 상태 계산
  const hasPromotionCodeError = !!errors.promotionCode;
  const hasPromotionCodeSuccess =
    !hasPromotionCodeError && !!checkPromotionCodeV2Res && isSuccess;
  const hasPromotionCodeValidationError =
    !!errors.promotionCode?.type && errors.promotionCode.type === "validation";

  const promotionCodeCheck = async () => {
    const currentPromotionCode = getValues("promotionCode");

    if (!currentPromotionCode || currentPromotionCode.trim() === "") {
      setError("promotionCode", {
        type: "validation",
        message: t("siteType.premium.detail.plan.invalidPromotionCode"),
      });
      return;
    }

    try {
      await checkPromotionMutation({
        promotionCode: currentPromotionCode,
        product: getProductFromSessionStorage() as ProductKey,
      });
    } catch (error) {
      // 에러는 useCheckPromotion 훅의 onError에서 처리됨
      console.error("프로모션 코드 확인 실패:", error);
    }
  };

  // 프로모션 적용 함수를 컴포넌트 내부에서 구현
  const promotionApply = async (promotionCode: string) => {
    try {
      await createServicePremiumBill({ promotionCode }).then((res) => {
        console.log("createServicePremiumBill", res);
        // 사이트 생성이 완료됐습니다.
        notifySuccess(t("siteManagement.createSite.siteCreatedBody"));
        // console.log("사이트 생성 완료");
        queryClient.clear(); // 쿼리 캐시 제거
        route.goConsole();
      });
    } catch (error: any) {
      if (error.errorCode && error.errorMessage) {
        notifyError(error.errorMessage);
      }
    }
  };

  const onSubmit: SubmitHandler<CheckPromotionFormData> = async (data) => {
    await promotionApply(data.promotionCode);
  };

  return (
    <FormProvider {...methods}>
      <form className="form" onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.containerLayout}>
          <div className={styles.promotionContainer}>
            <h4 className={styles.formTitle}>
              {t("siteType.premium.detail.plan.title")}
            </h4>
            <div className={styles.formItem}>
              <FormInput
                label={t("siteType.premium.detail.plan.promotionCode")}
                name="promotionCode"
                type="text"
                register={register}
                errors={errors}
                disabled={isSuccess}
                isClearBtn={false}
                placeholder={t(
                  "siteType.premium.detail.plan.enterPromotionCode"
                )}
                success={{
                  isSuccess: hasPromotionCodeSuccess,
                  message: t("siteType.premium.detail.plan.validPromotionCode"),
                }}
              >
                <button
                  type="button"
                  onClick={promotionCodeCheck}
                  disabled={
                    isLoading ||
                    !watchedPromotionCode ||
                    !!errors.promotionCode ||
                    isSuccess
                  }
                  className="btn btn-gray width mg ml-10 sendBtn"
                >
                  <span>
                    {isLoading ? (
                      <>
                        <Spinner animation="border" role="status" size="sm" />
                      </>
                    ) : (
                      t("siteType.premium.detail.plan.registerBtn")
                    )}
                  </span>
                </button>
              </FormInput>
              <ToggleAskPromotion
                toggleAskPromotionOn={toggleAskPromotionOn}
                setToggleAskPromotionOn={setToggleAskPromotionOn}
              />
            </div>

            {/* 프로모션 정보 표시 */}
            <PromotionInfo
              checkPromotionCodeV2Res={checkPromotionCodeV2Res}
              register={register}
            />
          </div>

          <RightDetailBox
            toggleAskPromotionOn={toggleAskPromotionOn}
            isCheckPromotionCode={
              hasPromotionCodeSuccess
                ? "OK"
                : hasPromotionCodeValidationError
                ? "FAIL"
                : ""
            }
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default CheckPromotion;
